/**
 * StatusBadge Component
 * Unified status badge with consistent colors and optional icons
 */
import { CheckCircle, Clock, XCircle, AlertTriangle, Pause, Archive, Play, Ban } from "lucide-react";
import { cn } from "@jourfx/ui";

export type StatusType =
  | "active"
  | "pending"
  | "locked"
  | "inactive"
  | "trial"
  | "suspended"
  | "expired"
  | "revoked"
  | "archived"
  | "disabled";

interface StatusBadgeProps {
  status: StatusType;
  size?: "sm" | "md";
  /** Show icon (default: true for better visual distinction) */
  showIcon?: boolean;
  className?: string;
  /** Override the display text */
  label?: string;
}

/**
 * Status configuration with design system colors
 * Per /DESIGN.md (Status Badge / semantic status colors):
 * - Success: Emerald 600 (#059669) / Emerald 400
 * - Warning: Amber 600 (#D97706) / Amber 400
 * - Error: Red 600 (#DC2626) / Red 400
 * - Info: Sky 600 (#0284C7) / Sky 400
 */
const statusConfig: Record<
  StatusType,
  {
    colors: string;
    icon: React.ComponentType<{ className?: string }>;
    defaultLabel: string;
  }
> = {
  active: {
    colors: "bg-success-muted text-success-muted-foreground border border-success-border",
    icon: CheckCircle,
    defaultLabel: "Aktiv",
  },
  pending: {
    colors: "bg-warning-muted text-warning-muted-foreground border border-warning-border",
    icon: Clock,
    defaultLabel: "Ausstehend",
  },
  locked: {
    colors: "bg-error-muted text-error-muted-foreground border border-error-border",
    icon: XCircle,
    defaultLabel: "Gesperrt",
  },
  inactive: {
    colors: "bg-muted text-muted-foreground border border-border",
    icon: Pause,
    defaultLabel: "Inaktiv",
  },
  trial: {
    colors: "bg-info-muted text-info-muted-foreground border border-info-border",
    icon: Play,
    defaultLabel: "Trial",
  },
  suspended: {
    colors: "bg-error-muted text-error-muted-foreground border border-error-border",
    icon: AlertTriangle,
    defaultLabel: "Suspendiert",
  },
  expired: {
    colors: "bg-error-muted text-error-muted-foreground border border-error-border",
    icon: XCircle,
    defaultLabel: "Abgelaufen",
  },
  revoked: {
    colors: "bg-error-muted text-error-muted-foreground border border-error-border",
    icon: Ban,
    defaultLabel: "Widerrufen",
  },
  archived: {
    colors: "bg-muted text-muted-foreground border border-border",
    icon: Archive,
    defaultLabel: "Archiviert",
  },
  disabled: {
    colors: "bg-muted text-muted-foreground/70 border border-border",
    icon: XCircle,
    defaultLabel: "Deaktiviert",
  },
};

export function StatusBadge({
  status,
  size = "md",
  showIcon = true,
  className,
  label,
}: StatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.inactive;
  const Icon = config.icon;
  const displayLabel = label || config.defaultLabel;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full font-medium",
        config.colors,
        size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-xs",
        className
      )}
    >
      {showIcon && <Icon className={cn(size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5")} />}
      {displayLabel}
    </span>
  );
}
